import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Handshake, Users, Building2, Globe, CheckCircle, Upload,
  Send, ChevronDown, FileText, Award, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const associateExpertiseAreas = [
  'Programme Development',
  'Research & Policy',
  'Climate Action',
  'Youth Empowerment',
  'Gender Equality',
  'Digital Advocacy',
  'Monitoring & Evaluation',
  'Fundraising'
];

const associateCollaborationInterests = [
  'Consultancy Support',
  'Project Collaboration',
  'Volunteer Coordination',
  'Advocacy Campaigns',
  'Training & Capacity Building'
];

const institutionalCollaborationAreas = [
  'Internship Programme Support',
  'Joint Programme Implementation',
  'Research Collaboration',
  'Funding & Resource Mobilization',
  'Community Development Projects',
  'Capacity Building & Training',
  'Climate Action Initiatives'
];

export default function PartnershipPage() {
  const [activeTab, setActiveTab] = useState('associate');
  
  // Associate Partnership Form State
  const [associateForm, setAssociateForm] = useState({
    full_name: '',
    organization_name: '',
    contact_person: '',
    email: '',
    phone: '',
    country: '',
    website: '',
    expertise_areas: [],
    collaboration_interests: [],
    motivation: '',
    cv_file: null
  });
  
  // Institutional Partnership Form State
  const [institutionalForm, setInstitutionalForm] = useState({
    organization_name: '',
    registration_number: '',
    year_established: '',
    country_of_registration: '',
    office_address: '',
    website: '',
    contact_name: '',
    contact_position: '',
    contact_email: '',
    contact_phone: '',
    partnership_type: '',
    collaboration_areas: [],
    organizational_experience: '',
    documents: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [associateFileName, setAssociateFileName] = useState('');
  const [institutionalFileNames, setInstitutionalFileNames] = useState([]);

  const handleAssociateChange = (e) => {
    const { name, value } = e.target;
    setAssociateForm(prev => ({ ...prev, [name]: value }));
  };

  const handleInstitutionalChange = (e) => {
    const { name, value } = e.target;
    setInstitutionalForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAssociateExpertiseToggle = (area) => {
    setAssociateForm(prev => ({
      ...prev,
      expertise_areas: prev.expertise_areas.includes(area)
        ? prev.expertise_areas.filter(a => a !== area)
        : [...prev.expertise_areas, area]
    }));
  };

  const handleAssociateCollaborationToggle = (interest) => {
    setAssociateForm(prev => ({
      ...prev,
      collaboration_interests: prev.collaboration_interests.includes(interest)
        ? prev.collaboration_interests.filter(i => i !== interest)
        : [...prev.collaboration_interests, interest]
    }));
  };

  const handleInstitutionalCollaborationToggle = (area) => {
    setInstitutionalForm(prev => ({
      ...prev,
      collaboration_areas: prev.collaboration_areas.includes(area)
        ? prev.collaboration_areas.filter(a => a !== area)
        : [...prev.collaboration_areas, area]
    }));
  };

  const handleAssociateFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAssociateForm(prev => ({ ...prev, cv_file: file }));
      setAssociateFileName(file.name);
    }
  };

  const handleInstitutionalFilesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setInstitutionalForm(prev => ({ ...prev, documents: files }));
      setInstitutionalFileNames(files.map(f => f.name));
    }
  };

  const handleAssociateSubmit = async (e) => {
    e.preventDefault();
    
    if (associateForm.expertise_areas.length === 0) {
      toast.error('Please select at least one area of expertise');
      return;
    }
    
    if (associateForm.collaboration_interests.length === 0) {
      toast.error('Please select at least one collaboration interest');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      Object.keys(associateForm).forEach(key => {
        if (key === 'cv_file') {
          // Only append file if it exists
          if (associateForm[key]) {
            formData.append('cv_file', associateForm[key]);
          }
        } else if (Array.isArray(associateForm[key])) {
          formData.append(key, JSON.stringify(associateForm[key]));
        } else if (associateForm[key] !== null && associateForm[key] !== undefined) {
          formData.append(key, associateForm[key]);
        } else {
          // For null/undefined values, send empty string for optional fields
          formData.append(key, '');
        }
      });
      
      await axios.post(`${API}/partnerships/associate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Associate Partnership application submitted successfully! We will review and contact you soon.');
      setAssociateForm({
        full_name: '',
        organization_name: '',
        contact_person: '',
        email: '',
        phone: '',
        country: '',
        website: '',
        expertise_areas: [],
        collaboration_interests: [],
        motivation: '',
        cv_file: null
      });
      setAssociateFileName('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInstitutionalSubmit = async (e) => {
    e.preventDefault();
    
    if (!institutionalForm.partnership_type) {
      toast.error('Please select a partnership type');
      return;
    }
    
    if (institutionalForm.collaboration_areas.length === 0) {
      toast.error('Please select at least one area of collaboration');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      Object.keys(institutionalForm).forEach(key => {
        if (key === 'documents') {
          // Only append files if they exist
          if (institutionalForm[key].length > 0) {
            institutionalForm[key].forEach(file => {
              formData.append('documents', file);
            });
          }
        } else if (Array.isArray(institutionalForm[key])) {
          formData.append(key, JSON.stringify(institutionalForm[key]));
        } else if (institutionalForm[key] !== null && institutionalForm[key] !== undefined) {
          formData.append(key, institutionalForm[key]);
        } else {
          // For null/undefined values, send empty string for optional fields
          formData.append(key, '');
        }
      });
      
      await axios.post(`${API}/partnerships/institutional`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Institutional Partnership application submitted successfully! Our team will review and contact you for MoU development.');
      setInstitutionalForm({
        organization_name: '',
        registration_number: '',
        year_established: '',
        country_of_registration: '',
        office_address: '',
        website: '',
        contact_name: '',
        contact_position: '',
        contact_email: '',
        contact_phone: '',
        partnership_type: '',
        collaboration_areas: [],
        organizational_experience: '',
        documents: []
      });
      setInstitutionalFileNames([]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="partnership-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-white/20 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/30 rounded-tr-[180px]" />
          <div className="absolute top-[30%] left-[15%] w-[150px] h-[150px] bg-[#E08B7A]/20 rounded-full" />
          <div className="absolute bottom-[20%] right-[10%] w-[100px] h-[100px] bg-white/15 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Handshake className="w-16 h-16 text-white mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
                Partnership with OPAD
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
                Together, we build sustainable partnerships that transform communities worldwide. 
                Join OPAD in creating lasting impact aligned with the UN Sustainable Development Goals.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partnership Image Banner */}
      <section className="relative overflow-hidden" data-testid="partnership-image-banner">
        <motion.div
          className="relative max-w-4xl mx-auto px-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_10eec864-0b4d-4948-b760-389570b9159b/artifacts/jtghdhmq_WhatsApp%20Image%202026-02-19%20at%2019.46.25.jpeg"
            alt="OPAD Partnership Meeting - Building Strategic Alliances"
            className="w-full h-auto object-cover rounded-2xl shadow-xl"
            data-testid="partnership-meeting-image"
          />
          <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg">
            <p className="text-center text-slate-700 font-medium">
              Building Strategic Partnerships for Sustainable Development
            </p>
          </div>
        </motion.div>
      </section>

      {/* Partnership Introduction */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[400px] h-[350px] bg-[#7BC4E2]/10 rounded-br-[180px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#E08B7A]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-lg text-slate-600 leading-relaxed">
                The Organization for Poverty Alleviation and Development (OPAD) welcomes collaboration 
                with individuals, institutions, corporate entities, and civil society organizations 
                that share our commitment to humanitarian action, sustainable development, and social 
                impact aligned with the United Nations Sustainable Development Goals (SDGs).
              </p>
            </motion.div>
          </div>

          {/* Partnership Types Cards */}
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className={`bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-2 cursor-pointer transition-all ${
                activeTab === 'associate' ? 'border-[#7BC4E2] ring-2 ring-[#7BC4E2]/20' : 'border-slate-100 hover:border-[#7BC4E2]/50'
              }`}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              onClick={() => setActiveTab('associate')}
              data-testid="associate-partnership-card"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-[#7BC4E2]/20 rounded-xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-[#7BC4E2]" />
                </div>
                <h3 className="text-xl font-bold font-['Manrope'] text-slate-900">
                  Associate Partnership
                </h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                Designed for individuals, independent consultants, youth leaders, researchers, 
                subject-matter experts, or small organizations who wish to collaborate with OPAD 
                in programme implementation, research, advocacy, volunteer mobilization, or 
                technical support on specific development initiatives.
              </p>
            </motion.div>

            <motion.div
              className={`bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-2 cursor-pointer transition-all ${
                activeTab === 'institutional' ? 'border-[#E08B7A] ring-2 ring-[#E08B7A]/20' : 'border-slate-100 hover:border-[#E08B7A]/50'
              }`}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              onClick={() => setActiveTab('institutional')}
              data-testid="institutional-partnership-card"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-[#E08B7A]/20 rounded-xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-[#E08B7A]" />
                </div>
                <h3 className="text-xl font-bold font-['Manrope'] text-slate-900">
                  Institutional / Corporate / Affiliate
                </h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                Formal collaborations between OPAD and legally registered organizations such as 
                universities, NGOs, government agencies, private companies, or civil society groups. 
                These partnerships aim to jointly implement development programmes, knowledge exchange, 
                and social impact campaigns.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Associate Partnership Section */}
      {activeTab === 'associate' && (
        <section className="relative section-padding overflow-hidden" data-testid="associate-section">
          <div className="absolute inset-0 bg-[#FDF8F4]">
            <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-[#7BC4E2]/10 rounded-bl-[250px]" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/10 rounded-tr-[180px]" />
          </div>
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              {/* Requirements */}
              <motion.div
                className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100 mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <Award className="w-6 h-6 text-[#7BC4E2]" />
                  <h3 className="text-xl font-bold font-['Manrope'] text-slate-900">
                    Associate Partnership Requirements
                  </h3>
                </div>
                <ul className="grid md:grid-cols-2 gap-3">
                  {[
                    'Demonstrate relevant expertise or interest in humanitarian or development work',
                    'Minimum of one year of experience in a relevant field (academic or professional)',
                    'Willingness to collaborate on OPAD programmes or initiatives',
                    'Commitment to OPAD\'s mission, values, and safeguarding policies',
                    'Ability to work independently or as part of a multidisciplinary team',
                    'Submission of a valid CV or organizational profile',
                    'Participation in an onboarding/orientation session'
                  ].map((req, index) => (
                    <li key={index} className="flex items-start gap-2 text-slate-600 text-sm">
                      <CheckCircle className="w-5 h-5 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Associate Application Form */}
              <motion.div
                className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-6 h-6 text-[#7BC4E2]" />
                  <h3 className="text-xl font-bold font-['Manrope'] text-slate-900">
                    Associate Partnership Application Form
                  </h3>
                </div>

                <form onSubmit={handleAssociateSubmit} data-testid="associate-form">
                  {/* Section A: Personal/Organizational Information */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-[#7BC4E2] uppercase tracking-wider mb-4">
                      Section A: Personal / Organizational Information
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="full_name">Full Name / Organization Name *</Label>
                        <Input
                          id="full_name"
                          name="full_name"
                          value={associateForm.full_name}
                          onChange={handleAssociateChange}
                          required
                          className="mt-1"
                          data-testid="associate-full-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_person">Contact Person</Label>
                        <Input
                          id="contact_person"
                          name="contact_person"
                          value={associateForm.contact_person}
                          onChange={handleAssociateChange}
                          className="mt-1"
                          data-testid="associate-contact-person"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={associateForm.email}
                          onChange={handleAssociateChange}
                          required
                          className="mt-1"
                          data-testid="associate-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={associateForm.phone}
                          onChange={handleAssociateChange}
                          required
                          className="mt-1"
                          data-testid="associate-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="country">Country of Residence *</Label>
                        <Input
                          id="country"
                          name="country"
                          value={associateForm.country}
                          onChange={handleAssociateChange}
                          required
                          className="mt-1"
                          data-testid="associate-country"
                        />
                      </div>
                      <div>
                        <Label htmlFor="website">Website / LinkedIn Profile</Label>
                        <Input
                          id="website"
                          name="website"
                          value={associateForm.website}
                          onChange={handleAssociateChange}
                          placeholder="https://"
                          className="mt-1"
                          data-testid="associate-website"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section B: Area of Expertise */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-[#7BC4E2] uppercase tracking-wider mb-4">
                      Section B: Area of Expertise *
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {associateExpertiseAreas.map((area) => (
                        <div key={area} className="flex items-center space-x-2">
                          <Checkbox
                            id={`expertise-${area}`}
                            checked={associateForm.expertise_areas.includes(area)}
                            onCheckedChange={() => handleAssociateExpertiseToggle(area)}
                            data-testid={`expertise-${area.toLowerCase().replace(/\s+/g, '-')}`}
                          />
                          <label 
                            htmlFor={`expertise-${area}`}
                            className="text-sm text-slate-600 cursor-pointer"
                          >
                            {area}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section C: Collaboration Interest */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-[#7BC4E2] uppercase tracking-wider mb-4">
                      Section C: Collaboration Interest *
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {associateCollaborationInterests.map((interest) => (
                        <div key={interest} className="flex items-center space-x-2">
                          <Checkbox
                            id={`collab-${interest}`}
                            checked={associateForm.collaboration_interests.includes(interest)}
                            onCheckedChange={() => handleAssociateCollaborationToggle(interest)}
                            data-testid={`collab-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                          />
                          <label 
                            htmlFor={`collab-${interest}`}
                            className="text-sm text-slate-600 cursor-pointer"
                          >
                            {interest}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section D: Motivation */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-[#7BC4E2] uppercase tracking-wider mb-4">
                      Section D: Motivation
                    </h4>
                    <Label htmlFor="motivation">Why would you like to partner with OPAD? *</Label>
                    <Textarea
                      id="motivation"
                      name="motivation"
                      value={associateForm.motivation}
                      onChange={handleAssociateChange}
                      required
                      rows={4}
                      placeholder="Share your motivation for partnering with OPAD..."
                      className="mt-1"
                      data-testid="associate-motivation"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="mb-8">
                    <Label>Upload CV / Organizational Profile</Label>
                    <div className="mt-2">
                      <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#7BC4E2] transition-colors">
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <span className="text-sm text-slate-600">
                            {associateFileName || 'Click to upload (PDF, DOC, DOCX)'}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleAssociateFileChange}
                          className="hidden"
                          data-testid="associate-cv-upload"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Declaration */}
                  <div className="mb-6">
                    <div className="flex items-start space-x-3">
                      <Checkbox id="associate-declaration" required data-testid="associate-declaration" />
                      <label htmlFor="associate-declaration" className="text-sm text-slate-600">
                        I hereby declare that the information provided is true and accurate. 
                        I agree to abide by OPAD's policies and values.
                      </label>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#7BC4E2] hover:bg-[#6AB3D1] text-white rounded-full py-6 font-semibold"
                    disabled={isSubmitting}
                    data-testid="associate-submit-button"
                  >
                    {isSubmitting ? (
                      <>Submitting...</>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Institutional Partnership Section */}
      {activeTab === 'institutional' && (
        <section className="relative section-padding overflow-hidden" data-testid="institutional-section">
          <div className="absolute inset-0 bg-[#FDF8F4]">
            <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-[#E08B7A]/10 rounded-bl-[250px]" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#7BC4E2]/10 rounded-tr-[180px]" />
          </div>
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              {/* Requirements */}
              <motion.div
                className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100 mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <Target className="w-6 h-6 text-[#E08B7A]" />
                  <h3 className="text-xl font-bold font-['Manrope'] text-slate-900">
                    Institutional / Corporate / Affiliate Partnership Requirements
                  </h3>
                </div>
                <ul className="grid md:grid-cols-2 gap-3">
                  {[
                    'Must be a legally registered organization',
                    'Provide certificate of registration or legal status',
                    'Minimum of two years of operational experience',
                    'Demonstrated alignment with OPAD\'s mission and SDG priorities',
                    'Willingness to enter into a Memorandum of Understanding (MoU)',
                    'Availability of focal person for coordination',
                    'Submission of organizational profile or portfolio',
                    'Commitment to transparency and ethical collaboration'
                  ].map((req, index) => (
                    <li key={index} className="flex items-start gap-2 text-slate-600 text-sm">
                      <CheckCircle className="w-5 h-5 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Institutional Application Form */}
              <motion.div
                className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-6 h-6 text-[#E08B7A]" />
                  <h3 className="text-xl font-bold font-['Manrope'] text-slate-900">
                    Institutional / Corporate / Affiliate Partnership Application
                  </h3>
                </div>

                <form onSubmit={handleInstitutionalSubmit} data-testid="institutional-form">
                  {/* Section A: Organization Details */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-4">
                      Section A: Organization Details
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="inst_org_name">Organization Name *</Label>
                        <Input
                          id="inst_org_name"
                          name="organization_name"
                          value={institutionalForm.organization_name}
                          onChange={handleInstitutionalChange}
                          required
                          className="mt-1"
                          data-testid="institutional-org-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="registration_number">Registration Number *</Label>
                        <Input
                          id="registration_number"
                          name="registration_number"
                          value={institutionalForm.registration_number}
                          onChange={handleInstitutionalChange}
                          required
                          className="mt-1"
                          data-testid="institutional-reg-number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="year_established">Year Established *</Label>
                        <Input
                          id="year_established"
                          name="year_established"
                          type="number"
                          min="1800"
                          max={new Date().getFullYear()}
                          value={institutionalForm.year_established}
                          onChange={handleInstitutionalChange}
                          required
                          className="mt-1"
                          data-testid="institutional-year"
                        />
                      </div>
                      <div>
                        <Label htmlFor="country_of_registration">Country of Registration *</Label>
                        <Input
                          id="country_of_registration"
                          name="country_of_registration"
                          value={institutionalForm.country_of_registration}
                          onChange={handleInstitutionalChange}
                          required
                          className="mt-1"
                          data-testid="institutional-country"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="office_address">Office Address *</Label>
                        <Input
                          id="office_address"
                          name="office_address"
                          value={institutionalForm.office_address}
                          onChange={handleInstitutionalChange}
                          required
                          className="mt-1"
                          data-testid="institutional-address"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="inst_website">Website</Label>
                        <Input
                          id="inst_website"
                          name="website"
                          value={institutionalForm.website}
                          onChange={handleInstitutionalChange}
                          placeholder="https://"
                          className="mt-1"
                          data-testid="institutional-website"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section B: Contact Person */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-4">
                      Section B: Contact Person
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact_name">Name *</Label>
                        <Input
                          id="contact_name"
                          name="contact_name"
                          value={institutionalForm.contact_name}
                          onChange={handleInstitutionalChange}
                          required
                          className="mt-1"
                          data-testid="institutional-contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_position">Position *</Label>
                        <Input
                          id="contact_position"
                          name="contact_position"
                          value={institutionalForm.contact_position}
                          onChange={handleInstitutionalChange}
                          required
                          className="mt-1"
                          data-testid="institutional-contact-position"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_email">Email Address *</Label>
                        <Input
                          id="contact_email"
                          name="contact_email"
                          type="email"
                          value={institutionalForm.contact_email}
                          onChange={handleInstitutionalChange}
                          required
                          className="mt-1"
                          data-testid="institutional-contact-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_phone">Phone Number *</Label>
                        <Input
                          id="contact_phone"
                          name="contact_phone"
                          type="tel"
                          value={institutionalForm.contact_phone}
                          onChange={handleInstitutionalChange}
                          required
                          className="mt-1"
                          data-testid="institutional-contact-phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section C: Type of Partnership */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-4">
                      Section C: Type of Partnership *
                    </h4>
                    <Select 
                      value={institutionalForm.partnership_type}
                      onValueChange={(value) => setInstitutionalForm(prev => ({ ...prev, partnership_type: value }))}
                    >
                      <SelectTrigger data-testid="institutional-partnership-type">
                        <SelectValue placeholder="Select partnership type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="institutional">Institutional Partnership</SelectItem>
                        <SelectItem value="corporate">Corporate Partnership</SelectItem>
                        <SelectItem value="affiliate">Affiliate Partnership</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Section D: Proposed Area of Collaboration */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-4">
                      Section D: Proposed Area of Collaboration *
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {institutionalCollaborationAreas.map((area) => (
                        <div key={area} className="flex items-center space-x-2">
                          <Checkbox
                            id={`inst-collab-${area}`}
                            checked={institutionalForm.collaboration_areas.includes(area)}
                            onCheckedChange={() => handleInstitutionalCollaborationToggle(area)}
                            data-testid={`inst-collab-${area.toLowerCase().replace(/\s+/g, '-')}`}
                          />
                          <label 
                            htmlFor={`inst-collab-${area}`}
                            className="text-sm text-slate-600 cursor-pointer"
                          >
                            {area}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section E: Organizational Experience */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-4">
                      Section E: Organizational Experience
                    </h4>
                    <Label htmlFor="organizational_experience">
                      Briefly describe your organization's experience relevant to OPAD's programmes *
                    </Label>
                    <Textarea
                      id="organizational_experience"
                      name="organizational_experience"
                      value={institutionalForm.organizational_experience}
                      onChange={handleInstitutionalChange}
                      required
                      rows={4}
                      placeholder="Describe your organization's relevant experience..."
                      className="mt-1"
                      data-testid="institutional-experience"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="mb-8">
                    <Label>Upload Supporting Documents</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Registration Certificate, Organizational Profile, Portfolio / Annual Report
                    </p>
                    <div className="mt-2">
                      <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#E08B7A] transition-colors">
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <span className="text-sm text-slate-600">
                            {institutionalFileNames.length > 0 
                              ? institutionalFileNames.join(', ')
                              : 'Click to upload multiple files (PDF, DOC, DOCX)'}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          multiple
                          onChange={handleInstitutionalFilesChange}
                          className="hidden"
                          data-testid="institutional-docs-upload"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Declaration */}
                  <div className="mb-6">
                    <div className="flex items-start space-x-3">
                      <Checkbox id="institutional-declaration" required data-testid="institutional-declaration" />
                      <label htmlFor="institutional-declaration" className="text-sm text-slate-600">
                        I hereby declare that the information provided is true and accurate. 
                        Our organization agrees to abide by OPAD's policies, values, and ethical collaboration standards.
                      </label>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full py-6 font-semibold"
                    disabled={isSubmitting}
                    data-testid="institutional-submit-button"
                  >
                    {isSubmitting ? (
                      <>Submitting...</>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                </form>
              </motion.div>

              {/* Review Notice */}
              <motion.div
                className="mt-8 text-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <p className="text-slate-600">
                  Applications will be reviewed by OPAD's Programme and Partnerships Department.
                  <br />
                  Shortlisted applicants will be contacted for further engagement and MoU development.
                </p>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6A9C79]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/10 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Globe className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-4">
              Together, We Transform Communities
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Join OPAD in building sustainable partnerships that create lasting impact worldwide.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
